import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Logo from "./Logo";
import { getSymbolImageUrl } from "../../lib/symbolImage";

// The link's hit area includes the cell padding, while preserving native link
// behavior (keyboard navigation, opening a new tab, and the browser context menu).
export const companyCellSx = {
  position: "relative",
  minWidth: 0,
  maxWidth: 0,
  "& .company-link::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    cursor: "pointer",
  },
  "& .company-link:focus-visible::after": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: -2,
  },
} as const;

interface CompanyIdentityProps {
  company?: string | null;
  symbol?: string | null;
  image?: string | null;
  metadata?: string;
  leading?: React.ReactNode;
  linked?: boolean;
}
export const CompanyIdentity = ({
  company,
  symbol,
  image,
  metadata,
  leading,
  linked = true,
}: CompanyIdentityProps) => {
  const ticker = symbol?.trim() ?? "";
  const hasTicker =
    !!ticker &&
    !["N/A", "NA", "--", "CASH", "ADJUSTMENT"].includes(ticker.toUpperCase());
  const companyName = company?.trim();
  const name = companyName
    ? companyName
    : hasTicker
      ? ticker
      : "Undisclosed asset";
  const content = (
    <>
      <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
        {leading}
        {!leading && (
          <Logo
            key={ticker}
            src={image ?? (hasTicker ? getSymbolImageUrl(ticker) : undefined)}
            alt=""
            ticker={ticker}
            companyName={name}
            size={28}
          />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          noWrap
          title={name}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.4,
          }}
        >
          {name}
        </Typography>
        <Typography
          noWrap
          sx={{
            fontSize: "0.8125rem",
            color: "text.secondary",
            lineHeight: 1.4,
          }}
        >
          {[hasTicker && ticker, metadata].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
    </>
  );
  const layout = {
    display: "flex",
    alignItems: "center",
    gap: 1,
    minWidth: 0,
    width: "100%",
    flex: 1,
  };
  if (!linked || !hasTicker) return <Box sx={layout}>{content}</Box>;
  return (
    <Link
      component={RouterLink}
      to={`/charts?ticker=${encodeURIComponent(ticker)}`}
      className="company-link"
      aria-label={`View ${name} (${ticker})`}
      underline="none"
      onClick={(event) => event.stopPropagation()}
      sx={{
        ...layout,
        color: "text.primary",
        cursor: "pointer",
        "&:hover": { color: "text.primary" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      {content}
    </Link>
  );
};
